import React, { useState, useEffect, useRef } from 'react';

interface PullToRefreshProps {
    onRefresh: () => Promise<void>;
    children: React.ReactNode;
    enabled?: boolean;
}

export const PullToRefresh: React.FC<PullToRefreshProps> = ({
    onRefresh,
    children,
    enabled = true
}) => {
    const [pullDistance, setPullDistance] = useState(0);
    const [isRefreshing, setIsRefreshing] = useState(false);
    const [isDragging, setIsDragging] = useState(false);

    const startYRef = useRef<number | null>(null);
    const currentYRef = useRef<number | null>(null);
    const containerRef = useRef<HTMLDivElement>(null);

    const threshold = 85; // Drag threshold to trigger refresh
    const maxPull = 140; // Max distance the loader can be dragged down

    useEffect(() => {
        if (!enabled) {
            setPullDistance(0);
            setIsDragging(false);
            return;
        }

        const handleTouchStart = (e: TouchEvent) => {
            if (window.scrollY > 5) return;
            
            // Check if any parent element of the target has scrolled down
            let el = e.target as HTMLElement | null;
            while (el && el !== containerRef.current) {
                if ((el.scrollTop || 0) > 5) return;
                el = el.parentElement;
            }

            startYRef.current = e.touches[0].clientY;
            currentYRef.current = e.touches[0].clientY;
        };

        const handleTouchMove = (e: TouchEvent) => {
            if (startYRef.current === null || isRefreshing) return;

            const currentY = e.touches[0].clientY;
            currentYRef.current = currentY;
            const diff = currentY - startYRef.current;

            // Only start tracking pull if the user pulls down beyond a small threshold
            if (diff > 5 && window.scrollY <= 5) {
                // Ensure no scroll container is scrolled
                let el = e.target as HTMLElement | null;
                let isScrolled = false;
                while (el && el !== containerRef.current) {
                    if ((el.scrollTop || 0) > 5) {
                        isScrolled = true;
                        break;
                    }
                    el = el.parentElement;
                }
                if (isScrolled) return;

                setIsDragging(true);
                if (e.cancelable) {
                    e.preventDefault();
                }
                const dampening = 0.45;
                const distance = Math.min(maxPull, diff * dampening);
                setPullDistance(distance);
            }
        };

        const handleTouchEnd = async () => {
            if (startYRef.current === null) return;

            const wasDragging = isDragging;
            setIsDragging(false);

            const startY = startYRef.current;
            const currentY = currentYRef.current || startY;
            const diff = currentY - startY;
            const dampening = 0.45;
            const finalDistance = diff * dampening;

            startYRef.current = null;
            currentYRef.current = null;

            if (wasDragging && finalDistance >= threshold && !isRefreshing) {
                setIsRefreshing(true);
                setPullDistance(60); // Snap to comfortable loading height
                try {
                    await onRefresh();
                } catch (err) {
                    console.error('Error refreshing:', err);
                } finally {
                    setIsRefreshing(false);
                    setPullDistance(0);
                }
            } else {
                setPullDistance(0);
            }
        };

        const handleMouseDown = (e: MouseEvent) => {
            if (window.scrollY > 5) return;
            
            // Check if any parent element of the target has scrolled down
            let el = e.target as HTMLElement | null;
            while (el && el !== containerRef.current) {
                if ((el.scrollTop || 0) > 5) return;
                el = el.parentElement;
            }

            startYRef.current = e.clientY;
            currentYRef.current = e.clientY;
        };

        const handleMouseMove = (e: MouseEvent) => {
            if (startYRef.current === null || isRefreshing) return;
            const currentY = e.clientY;
            currentYRef.current = currentY;
            const diff = currentY - startYRef.current;

            if (diff > 5 && window.scrollY <= 5) {
                // Ensure no scroll container is scrolled
                let el = e.target as HTMLElement | null;
                let isScrolled = false;
                while (el && el !== containerRef.current) {
                    if ((el.scrollTop || 0) > 5) {
                        isScrolled = true;
                        break;
                    }
                    el = el.parentElement;
                }
                if (isScrolled) return;

                setIsDragging(true);
                const dampening = 0.45;
                const distance = Math.min(maxPull, diff * dampening);
                setPullDistance(distance);
            }
        };

        const handleMouseUp = async () => {
            if (startYRef.current === null) return;

            const wasDragging = isDragging;
            setIsDragging(false);

            const startY = startYRef.current;
            const currentY = currentYRef.current || startY;
            const diff = currentY - startY;
            const dampening = 0.45;
            const finalDistance = diff * dampening;

            startYRef.current = null;
            currentYRef.current = null;

            if (wasDragging && finalDistance >= threshold && !isRefreshing) {
                setIsRefreshing(true);
                setPullDistance(60);
                try {
                    await onRefresh();
                } catch (err) {
                    console.error('Error refreshing:', err);
                } finally {
                    setIsRefreshing(false);
                    setPullDistance(0);
                }
            } else {
                setPullDistance(0);
            }
        };

        const target = containerRef.current;
        if (target) {
            target.addEventListener('touchstart', handleTouchStart, { passive: true });
            target.addEventListener('touchmove', handleTouchMove, { passive: false });
            target.addEventListener('touchend', handleTouchEnd);

            target.addEventListener('mousedown', handleMouseDown);
            window.addEventListener('mousemove', handleMouseMove);
            window.addEventListener('mouseup', handleMouseUp);
        }

        return () => {
            if (target) {
                target.removeEventListener('touchstart', handleTouchStart);
                target.removeEventListener('touchmove', handleTouchMove);
                target.removeEventListener('touchend', handleTouchEnd);

                target.removeEventListener('mousedown', handleMouseDown);
            }
            window.removeEventListener('mousemove', handleMouseMove);
            window.removeEventListener('mouseup', handleMouseUp);
        };
    }, [enabled, isRefreshing, isDragging, onRefresh]);

    // Calculate rotation angle proportional to pull distance (up to threshold)
    const rotationAngle = Math.min(360, (pullDistance / threshold) * 360);

    const isPullingActive = pullDistance > 0 || isDragging || isRefreshing;

    return (
        <div ref={containerRef} className="relative w-full h-full min-h-full">
            {/* Loading Indicator Area */}
            <div
                className="absolute left-0 right-0 flex items-center justify-center pointer-events-none z-50 overflow-hidden"
                style={{
                    height: `${pullDistance}px`,
                    top: 0,
                    opacity: pullDistance > 10 ? Math.min(1, (pullDistance - 10) / 40) : 0,
                    transition: isDragging ? 'none' : 'height 0.3s cubic-bezier(0.19, 1, 0.22, 1), opacity 0.3s cubic-bezier(0.19, 1, 0.22, 1)'
                }}
            >
                <div className="flex flex-col items-center justify-center">
                    <img
                        src="/assets/goodieboy.png"
                        alt="Loading..."
                        className={`w-10 h-10 object-contain ${isRefreshing ? 'animate-spin' : ''}`}
                        style={{
                            transform: isRefreshing ? undefined : `rotate(${rotationAngle}deg)`,
                            transition: isDragging || isRefreshing ? 'none' : 'transform 0.3s cubic-bezier(0.19, 1, 0.22, 1)'
                        }}
                    />
                </div>
            </div>

            {/* Elastic Shift Content Wrapper */}
            <div
                style={
                    isPullingActive
                        ? {
                              transform: `translateY(${pullDistance}px)`,
                              transition: isDragging ? 'none' : 'transform 0.3s cubic-bezier(0.19, 1, 0.22, 1)'
                          }
                        : undefined
                }
                className="w-full h-full min-h-full"
            >
                {children}
            </div>
        </div>
    );
};
